import React from 'react';
import {
  Box,
  Container,
  Grid,
  Typography,
  Card,
  Paper,
  Avatar,
  Chip,
  useTheme,
} from '@mui/material';
import {
  Phone,
  Email,
  LocationOn,
  Schedule,
  CheckCircle,
} from '@mui/icons-material';
import { styled, createTheme, ThemeProvider } from '@mui/material/styles';

// --- MOCK THEME (Applying the sharp, low-radius corporate look) ---
const mockTheme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: '#0A3B53', // Dark Blue for a professional touch
      light: '#3AC3E3', // Bright Cyan/Blue for accents
      dark: '#072F42',
    },
    secondary: {
      main: '#F48C06', // Orange for highlight
    },
    background: {
      default: '#FFFFFF', // White background
      paper: '#F8F9FA', // Light grey for cards/sections
    },
    text: {
      primary: '#212529', // Dark text for contrast
      secondary: '#6C757D', // Muted text for descriptions
    },
  },
  typography: {
    fontFamily: 'Inter, Arial, sans-serif',
    h2: {
      fontSize: '3.5rem',
      '@media (max-width:900px)': {
        fontSize: '2.5rem',
      },
      '@media (max-width:600px)': {
        fontSize: '2rem',
      },
    },
    h3: {
      fontSize: '2.8rem',
      '@media (max-width:900px)': {
        fontSize: '2rem',
      },
      '@media (max-width:600px)': {
        fontSize: '1.75rem',
      },
    },
    h4: {
      fontSize: '2rem',
      '@media (max-width:900px)': {
        fontSize: '1.5rem',
      },
    },
    h6: {
      fontSize: '1.25rem',
      '@media (max-width:600px)': {
        fontSize: '1rem',
      },
    }
  },
  components: {
    // Reduced border radius to 3 for a sharper, less friendly look
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          borderRadius: 3,
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 3,
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 3, // Sharp card borders
        }
      }
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 3, // Sharp paper borders
        }
      }
    }
  }
});

// --- MOCK COMPONENTS (Needed for Single File Mandate) ---

// 1. Mock Footer Component
const Footer = () => (
  <Box sx={{
    backgroundColor: 'background.paper',
    color: 'text.secondary',
    py: 4,
    textAlign: 'center',
    borderTop: `1px solid ${mockTheme.palette.divider}`
  }}>
    <Container maxWidth="lg">
      <Typography variant="body2">
        &copy; {new Date().getFullYear()} Brothers Fiber Construction. All Rights Reserved.
      </Typography>
    </Container>
  </Box>
);

// --- STYLED COMPONENTS ---

const HeroSection = styled(Box)(({ theme }) => ({
  background: `linear-gradient(135deg, ${theme.palette.primary.main}10 0%, ${theme.palette.background.default} 50%)`,
  padding: theme.spacing(12, 2, 8, 2),
  textAlign: 'center',
  position: 'relative',
  overflow: 'hidden',
  color: theme.palette.text.primary,
}));

const ContactInfoItemCard = styled(Card)(({ theme }) => ({
  padding: theme.spacing(3),
  transition: 'all 0.3s ease',
  backgroundColor: theme.palette.background.paper,
  boxShadow: theme.shadows[2],
  borderRadius: theme.shape.borderRadius, // Uses the sharp theme value (3)
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  textAlign: 'center',
  borderBottom: `4px solid transparent`,
  '&:hover': {
    transform: 'translateY(-6px)',
    boxShadow: `0 15px 40px ${theme.palette.primary.main}10`,
    borderBottom: `4px solid ${theme.palette.primary.light}`,
  }
}));

// --- MAIN APPLICATION COMPONENT ---

function App() {
  const theme = useTheme();

  // **B2B Contact Content**
  const contactMethods = [
    {
      icon: <Phone />,
      title: "Commercial Line",
      content: "+1 515 850 9012",
      description: "Speak with our dedicated partnership team for new projects"
    },
    {
      icon: <Email />,
      title: "Project Inquiries",
      content: "projects@brothersfiber.com",
      description: "Send us your RFQ or project specification documents"
    },
    {
      icon: <LocationOn />,
      title: "Headquarters",
      content: "Marlborough, MA 01752",
      subContent: "243 Boston Post RD E APT 2",
      description: "Visits by appointment only for project discussions"
    },
    {
      icon: <Schedule />,
      title: "Office Hours",
      content: "Mon - Fri: 8AM - 6PM EST",
      subContent: "Appointments available upon request",
      description: "We align with your schedule for critical infrastructure planning"
    }
  ];

  // **B2B FAQ Content**
  const faqs = [
    {
      question: "What is the typical timeline for a large-scale fiber deployment?",
      answer: "Project timelines are scoped per contract complexity, generally ranging from 4 to 12 weeks for enterprise and municipal fiber infrastructure deployment."
    },
    {
      question: "Which regions do you cover for B2B fiber construction projects?",
      answer: "We offer regional and nationwide deployment capabilities. Contact our team to confirm resource availability for your specific project location."
    },
    {
      question: "What Service Level Agreements (SLAs) are available for business partners?",
      answer: "We offer customizable SLAs based on project scope, including uptime guarantees, dedicated support channels, and rapid response times for critical infrastructure."
    }
  ];

  return (
    <ThemeProvider theme={mockTheme}>
      <Box sx={{ backgroundColor: 'background.default', minHeight: '100vh', color: 'text.primary' }}>

        {/* 1. Hero Section - Focus on Enterprise */}
        <HeroSection>
          <Container maxWidth="lg">
            <Chip
              label="B2B FIBER INFRASTRUCTURE"
              color="primary"
              variant="outlined"
              sx={{
                mb: 3,
                fontWeight: 600,
                fontSize: '0.85rem',
                padding: '4px 12px',
                borderColor: theme.palette.primary.main,
                color: theme.palette.primary.main,
              }}
            />
            <Typography
              variant="h2"
              sx={{
                fontWeight: 800,
                mb: 2,
                lineHeight: 1.1,
                color: theme.palette.text.primary,
              }}
            >
              Partner with Our Enterprise Team
            </Typography>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 400,
                mb: 6,
                color: theme.palette.text.secondary,
                maxWidth: 700,
                mx: 'auto',
                lineHeight: 1.6
              }}
            >
              We focus exclusively on providing mission-critical fiber construction and solutions for service providers, municipalities, and large enterprises across the country.
            </Typography>
          </Container>
        </HeroSection>

        {/* 2. Contact Methods - FULL WIDTH AND SYMMETRICAL */}
        <Container maxWidth="lg" sx={{ py: 10 }}>
          <Box sx={{ mb: 6, textAlign: 'center' }}>
            <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
              Get in Touch Directly
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Our lines are open for project scoping and initial consultations.
            </Typography>
          </Box>
          
          {/* Contact Cards Grid (Full Width) */}
          <Grid container spacing={3} justifyContent="center" alignItems="stretch">
            {contactMethods.map((method, index) => (
              <Grid item xs={12} sm={6} md={3} key={index} sx={{ display: 'flex' }}> {/* md={3} ensures 4 cards fit evenly */}
                <ContactInfoItemCard>
                  <Avatar
                    sx={{
                      backgroundColor: theme.palette.primary.dark,
                      color: theme.palette.background.default,
                      width: 50,
                      height: 50,
                      mb: 2,
                      boxShadow: theme.shadows[3],
                    }}
                  >
                    {method.icon}
                  </Avatar>
                  <Typography
                    variant="subtitle1"
                    sx={{
                      fontWeight: 600,
                      color: theme.palette.primary.main,
                      mb: 0.5,
                    }}
                  >
                    {method.title}
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{
                      fontWeight: 700,
                      mb: 0.5,
                      color: theme.palette.text.primary,
                    }}
                  >
                    {method.content}
                  </Typography>
                  {method.subContent && (
                    <Typography
                      variant="body2"
                      sx={{
                        color: theme.palette.text.secondary,
                        mb: 1,
                      }}
                    >
                      {method.subContent}
                    </Typography>
                  )}
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mt: 1 }}
                  >
                    {method.description}
                  </Typography>
                </ContactInfoItemCard>
              </Grid>
            ))}
          </Grid>
        </Container>

        {/* 3. FAQ Section */}
        <Box sx={{ py: 10, backgroundColor: theme.palette.background.paper }}>
          <Container maxWidth="md">
            <Box sx={{ textAlign: 'center', mb: 6 }}>
              <Typography
                variant="h3"
                sx={{
                  fontWeight: 800,
                  mb: 1,
                  color: theme.palette.text.primary,
                }}
              >
                Enterprise Project FAQs
              </Typography>
              <Typography
                variant="body1"
                color="text.secondary"
                sx={{ maxWidth: 600, mx: 'auto' }}
              >
                Find answers to common questions about our B2B fiber deployment services and contracts.
              </Typography>
            </Box>

            <Grid container spacing={3}>
              {faqs.map((faq, index) => (
                <Grid item xs={12} key={index}>
                  <Paper sx={{ p: 3, backgroundColor: theme.palette.background.default, borderRadius: theme.shape.borderRadius, boxShadow: theme.shadows[1] }}>
                    <Typography
                      variant="subtitle1"
                      sx={{
                        fontWeight: 700,
                        mb: 1,
                        color: theme.palette.primary.main,
                        display: 'flex',
                        alignItems: 'flex-start'
                      }}
                    >
                      <CheckCircle color="primary" sx={{ mr: 1, fontSize: '1.2rem', mt: '3px', flexShrink: 0 }} />
                      {faq.question}
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ lineHeight: 1.8, pl: 3.5 }}
                    >
                      {faq.answer}
                    </Typography>
                  </Paper>
                </Grid>
              ))}
            </Grid>
          </Container>
        </Box>

        <Footer />
      </Box>
    </ThemeProvider>
  );
}

// Export the main component wrapped in the ThemeProvider for the single file structure
export default App;
